import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../store/authStore';
import { isSuperAdmin } from '../../../lib/roles';
import { formatDisplayShort } from '../../../lib/dateUtils';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Badge from '../../../components/ui/Badge';
import StatTile from '../../../components/ui/StatTile';
import SettingsCard from '../../../components/ui/SettingsCard';
import {
  usePlatformAdminsList,
  useGrantPlatformAdmin,
  useRevokePlatformAdmin,
} from '../hooks/usePlatformAdmins';

/**
 * Platform super-admin admins screen. Gated like SuperAdminOrganizations:
 * redirect non-super-admins; queries enabled only when permitted.
 */
export default function PlatformAdmins() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const allowed = isSuperAdmin(user);

  const [email, setEmail] = useState('');

  useEffect(() => {
    if (user !== null && !isSuperAdmin(user)) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const { data, isLoading, error } = usePlatformAdminsList({ enabled: allowed });

  const grantMutation = useGrantPlatformAdmin({
    onSuccess: () => setEmail(''),
  });

  const revokeMutation = useRevokePlatformAdmin();

  const admins = data?.admins ?? [];
  const onlyOneAdmin = admins.length === 1;

  const handleGrant = (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || grantMutation.isLoading) return;
    grantMutation.mutate({ email: trimmed });
  };

  const handleRevoke = (admin) => {
    const name = [admin.first_name, admin.last_name].filter(Boolean).join(' ') || admin.email;
    if (!window.confirm(`Are you sure you want to revoke platform admin access for ${name}?`)) {
      return;
    }
    revokeMutation.mutate(admin.id);
  };

  if (user !== null && !allowed) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="page team-page">
        <p className="text-muted" style={{ padding: '2rem', textAlign: 'center' }}>
          Loading platform admins…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page team-page">
        <header className="settings-tab-header settings-tab-header--split">
          <h1 className="settings-tab-title">Platform admins</h1>
        </header>
        <p className="text-muted" style={{ padding: '2rem', textAlign: 'center' }}>
          Failed to load platform admins.
        </p>
      </div>
    );
  }

  return (
    <div className="page team-page">
      <header className="settings-tab-header settings-tab-header--split">
        <h1 className="settings-tab-title">Platform admins</h1>
      </header>

      <p className="text-muted" style={{ marginBottom: '1rem' }}>
        Platform admins can create and suspend any organisation. This does not give them
        access to another organisation&apos;s data.
      </p>

      <div className="settings-stat-row">
        <Card>
          <StatTile value={admins.length} label="Platform admins" />
        </Card>
      </div>

      <SettingsCard
        title="Grant access"
        hint="The person must already have a FieldCert account — granting only elevates an existing user."
      >
        <form onSubmit={handleGrant}>
          <Input
            id="grant-admin-email"
            type="email"
            label="Email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            aria-label="Email"
          />
          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              disabled={grantMutation.isLoading || !email.trim()}
            >
              {grantMutation.isLoading ? 'Granting…' : 'Grant access'}
            </Button>
          </div>
        </form>
      </SettingsCard>

      <AdminsList
        admins={admins}
        onlyOneAdmin={onlyOneAdmin}
        onRevoke={handleRevoke}
        isRevoking={revokeMutation.isLoading}
      />
    </div>
  );
}

function revokeDisabledReason(admin, onlyOneAdmin) {
  if (admin.isSelf) return 'You cannot remove your own platform access';
  if (onlyOneAdmin) return 'At least one platform admin must remain';
  return null;
}

function RevokeButton({ admin, onlyOneAdmin, onRevoke, isRevoking }) {
  const reason = revokeDisabledReason(admin, onlyOneAdmin);
  const disabled = Boolean(reason) || isRevoking;
  const label = admin.first_name || admin.email;

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => onRevoke(admin)}
      disabled={disabled}
      title={reason || 'Revoke platform admin access'}
      aria-label={reason || `Revoke access for ${label}`}
    >
      Revoke
    </Button>
  );
}

function AdminsList({ admins, onlyOneAdmin, onRevoke, isRevoking }) {
  const rows = admins || [];

  return (
    <div className="card team-card">
      <div className="team-table-desktop">
        <table className="table team-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Organisation</th>
              <th>Last login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((admin) => (
              <tr key={admin.id}>
                <td>
                  {[admin.first_name, admin.last_name].filter(Boolean).join(' ') || '—'}
                  {admin.isSelf && (
                    <>
                      {' '}
                      <Badge variant="accent">You</Badge>
                    </>
                  )}
                </td>
                <td>{admin.email || '—'}</td>
                <td>{admin.organization_name || '—'}</td>
                <td>
                  {admin.last_login_at ? formatDisplayShort(admin.last_login_at) : '—'}
                </td>
                <td>
                  <div className="team-table-actions">
                    <RevokeButton
                      admin={admin}
                      onlyOneAdmin={onlyOneAdmin}
                      onRevoke={onRevoke}
                      isRevoking={isRevoking}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="team-mobile-cards">
        {rows.map((admin) => (
          <div key={admin.id} className="team-mobile-card">
            <div className="team-mobile-card-name">
              {[admin.first_name, admin.last_name].filter(Boolean).join(' ') || '—'}
              {admin.isSelf && (
                <>
                  {' '}
                  <Badge variant="accent">You</Badge>
                </>
              )}
            </div>
            <div className="team-mobile-card-email">{admin.email || '—'}</div>
            <div className="team-mobile-card-role text-muted">
              {admin.organization_name || '—'}
            </div>
            <div className="team-mobile-card-status text-muted">
              {admin.last_login_at
                ? `Last login ${formatDisplayShort(admin.last_login_at)}`
                : 'Never logged in'}
            </div>
            <div className="team-mobile-card-actions">
              <RevokeButton
                admin={admin}
                onlyOneAdmin={onlyOneAdmin}
                onRevoke={onRevoke}
                isRevoking={isRevoking}
              />
            </div>
          </div>
        ))}
      </div>

      {rows.length === 0 && (
        <p className="empty-state">No platform admins yet.</p>
      )}
    </div>
  );
}
